import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { HOT_JAR_TAG } from 'b2b-apps/shop/analytics/types/types';
import { tagHotJarRecording } from 'b2b-apps/shop/analytics/utils/analytics';
import { DATE_CONNECTOR_TYPE, DATE_TYPE, getDate, getDaysDifference } from 'b2b-apps/shop/common/utils/date/date';
import { getCookie, setCookie } from 'b2b-apps/shop/common/utils/storage/cookies';
import { CONTENT_COLLECTION, useCollection, useEntriesMap } from 'b2b-apps/shop/content/hooks';
import { useActiveLanguage, useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { useAccessCallbackData } from 'b2b-apps/shop/routes/hooks';
import { getCurrentRoute } from 'b2b-apps/shop/routes/redux/selectors';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const COOKIE_FEATURE_ONBOARDING = 'feature_onboarding';
const DEFAULT_DAYS_TO_EXPIRE = 365;

export const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement | null => {
    const { pathname } = useLocation();
    const lang = useActiveLanguage();
    const { data: entries } = useEntriesMap();
    const accessCallbackData = useAccessCallbackData();
    const content = useCollection(
      CONTENT_COLLECTION.onboarding,
      {
        show_message: 'Yes',
        show_until: {
          gte: getDate(
            new Date().toISOString(),
            DATE_TYPE.YMD,
            DATE_CONNECTOR_TYPE.dash,
          ),
        },
      },
    );
    const { data, isLoaded } = content;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const currentRoute = useMemo(() => getCurrentRoute(entries, pathname, lang),
      [entries, pathname, lang]);

    const isAllowed = useMemo(() => !!currentRoute?.allow?.(accessCallbackData),
      [currentRoute, accessCallbackData]);

    const getCookieKey = useCallback(
      (entryId: string) => `${COOKIE_FEATURE_ONBOARDING}_${entryId}_${lang}`,
      [lang],
    );

    const getCookieDaysToExpire = useCallback((showUntil?: string) => {
      if (showUntil) {
        return (
          getDaysDifference(
            getDate(new Date().toISOString()),
            getDate(showUntil),
          ) + 1
        );
      }

      return DEFAULT_DAYS_TO_EXPIRE;
    }, []);

    const currentEntry = useMemo(() => {
      if (!isLoaded || !data) {
        return null;
      }

      const filteredData = data.filter((entry) => {
        const match = matchPath(pathname, {
          path: `/${lang}${entry.data.url}`,
          strict: false,
          sensitive: true,
        });
        return match !== null;
      });

      if (filteredData.length > 0) {
        const entry = filteredData.sort(
          (entry1, entry2) => Date.parse(entry2.updatedAt) - Date.parse(entry1.updatedAt),
        )[0];
        const cookie = (entry ? getCookie(getCookieKey(entry.entryId)) : null) as Record<string, any>;

        if (!cookie || cookie.isVisible) {
          return entry;
        }
      }

      return null;
    }, [getCookieKey, pathname, isLoaded, data, lang]);

    const onDismiss = useCallback(() => {
      setIsModalOpen(false);

      if (currentEntry) {
        setCookie(
          getCookieKey(currentEntry.entryId),
          { isVisible: false },
          getCookieDaysToExpire(currentEntry.data.show_until),
        );
      }
    }, [currentEntry, getCookieKey, getCookieDaysToExpire]);

    useEffect(() => {
      if (currentEntry) {
        setIsModalOpen(true);
        tagHotJarRecording(HOT_JAR_TAG.featureOnboardingOpen);
      }
    }, [currentEntry]);

    const t = useTranslate();
    const modalButtonLabel = t('Close');

    const injectedProps: InjectedProps = {
      entry: currentEntry,
      onDismiss,
      isOpen: isAllowed && isModalOpen,
      modalButtonLabel,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return WithContainer;
};
